import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  UserState, 
  AppUser, 
  FamilyAccount, 
  UserSession,
  createDefaultUserSession,
  createDefaultUserPreferences,
  createDefaultAppState,
  getDefaultProfilePermissions
} from '../types/user';
import type {
  LoginRequestDto,
  LoginResponseDto,
  UserInfoDto,
} from '../types/api';
import type {
  RefreshTokenResponseDto,
  ProfileGetListResponseDto,
  AdminHistoryGetListResponseDto,
  AdminHistoryGetListItemDto,
} from '../types/api/authentication';
import { ApiService } from '../services/api';
import { StorageInterface } from './childNavStores';
import type { AppProfileDto } from '../types/api/appProfile';
import { convertAppProfileToChildProfile } from '../types/profile';
import { AppProfileStorage } from '../services/AppProfileStorage';
import { AdminResponseStorage } from '../storage/AdminResponseStorage';
import type { AdminResponseDto } from '../types/AdminResponseDto';
import { AccountUserStorage } from '../storage/AccountUserStorage';
import type { AuthorizedUserDto } from '../types/AuthorizedUserDto';
import { ConnectionStorage } from '../storage/ConnectionStorage';
import type { ConnectionDto } from '../types/ConnectionDto';
import type { AccountGetInfoResponseDto, AccountConnectionDto } from '../types/api/service';
import { applyUserSettingsFromDto } from '../utils/applyUserSettings';

interface UserStoreConfig {
  storage: StorageInterface;
  apiService?: ApiService;
  autoLogin?: boolean;
}

/**
 * Maps UserInfoDto from backend to AppUser for client state
 */
function mapUserInfoDtoToAppUser(userInfo: UserInfoDto, email: string): AppUser {
  // Enhanced userId mapping with fallback logic
  const mappedUserId = userInfo.sraId || userInfo.accountId || email || 'fallback-user-id';
  const isWebRuntime = (() => {
    try {
      if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
        return false;
      }
      return typeof document !== 'undefined';
    } catch {
      return false;
    }
  })();

  return {
    userId: mappedUserId,
    familyId: userInfo.accountId,
    name: userInfo.name,
    email: email,
    role: 'primary', // Default role, could be enhanced based on backend data
    authToken: undefined, // Will be set separately
    refreshToken: undefined, // Will be set separately
    lastLoginDate: new Date().toISOString(),
    preferences: createDefaultUserPreferences(),
    appState: createDefaultAppState(),
    profilePermissions: {},
    createdDate: userInfo.createdAt || new Date().toISOString(),
    lastActiveDate: new Date().toISOString(),
    deviceInfo: {
      platform: isWebRuntime ? 'web' as const : 'mobile' as const,
      appVersion: '1.0.0',
    },
  };
}

/**
 * Creates a FamilyAccount from UserInfoDto
 */
function createFamilyAccountFromUserInfo(userInfo: UserInfoDto): FamilyAccount {
  return {
    familyId: userInfo.accountId,
    familyName: `${userInfo.name}'s Family`, // Default name
    accountTier: 'standard', // Default tier
    subscriptionStatus: userInfo.isActive === '1' ? 'active' : 'inactive',
    createdDate: userInfo.createdAt || new Date().toISOString(),
    lastActiveDate: new Date().toISOString(),
    settings: {
      dataRetentionDays: 365,
      allowDataSharing: false,
      backupEnabled: true,
      syncEnabled: true,
    },
  };
}

/**
 * Converts an AccountConnectionDto (numeric API codes) to the ProfileConnectionDto
 * (human-readable strings) used in ChildProfile.connections.
 */
function accountConnDtoToProfileConn(dto: AccountConnectionDto) {
  return {
    ConnId: dto.PatConnId || '',
    DName: dto.DName,
    Email: dto.Type === '0' ? dto.Link : '',
    Type: dto.Type === '1' ? 'Linked' : 'Secure Email',
    Permissions:
      dto.Permissions === '2' ? 'Full Access' :
      dto.Permissions === '1' ? 'Assessments & Reports' : 'Notifications Only',
    Primary: dto.Primary === '1' ? 'true' : '',
    Status: dto.Status,
  };
}

/**
 * Applies connections from AccountGetInfoResponseDto.connections to the profile store.
 * Groups connections by ProfileId and calls updateProfile for each profile.
 * Non-fatal — logs warnings but never throws.
 */
function applyAccountConnectionsToProfileStore(
  connections: AccountConnectionDto[] | undefined,
  profileStore: any,
  callerLabel = 'unknown'
): void {
  if (!connections?.length || !profileStore) {
    return;
  }
  try {
    const byProfile = new Map<string, AccountConnectionDto[]>();
    for (const conn of connections) {
      if (!conn.ProfileId) {
        continue;
      }
      const list = byProfile.get(conn.ProfileId) ?? [];
      list.push(conn);
      byProfile.set(conn.ProfileId, list);
    }
    const storeState = profileStore.getState?.();
    if (!storeState || typeof storeState.updateProfile !== 'function') {
      return;
    }
    const storeProfileIds = (storeState.profiles ?? []).map((p: any) => p.id);
    let appliedCount = 0;
    for (const [profileId, conns] of byProfile.entries()) {
      const exists = storeProfileIds.includes(profileId);
      if (!exists) {
        continue;
      }
      const mapped = conns.map(accountConnDtoToProfileConn);
      storeState.updateProfile(profileId, { connections: mapped });
      appliedCount++;
    }
  } catch (err) {
    console.warn(`⚠️ UserStore: applyAccountConnectionsToProfileStore [${callerLabel}] failed (non-fatal):`, err);
  }
}

// User store implementation
export const createUserStore = (config: UserStoreConfig) => {
  return create<UserState>()(
    subscribeWithSelector((set, get) => ({
      // Current user session
      session: createDefaultUserSession(),
      
      // Family management
      familyUsers: [],
      
      // Authentication actions
      login: async (
        email: string, 
        password: string, 
        signature?: string, 
        accessType?: string, 
        skinType?: string,
        profileStore?: any  // Optional profile store for sync after login
      ): Promise<boolean> => {
        try {
          set((state) => ({ ...state, session: { ...state.session, isLoading: true } }));
          
          // Prepare login request
          const loginRequest: LoginRequestDto = {
            Email: email,
            Password: password,
            Signature: signature || 'testsignature123',
            AccessType: accessType || '5',
            SkinType: skinType || '1',
          };
          
          // Authenticate with backend
          if (!config.apiService) {
            throw new Error('ApiService not configured');
          }
          const loginResponse: LoginResponseDto = await config.apiService.authenticate(loginRequest);
          
          if (loginResponse.success && loginResponse.data) {
            
            // Map backend user data to client models
            const appUser = mapUserInfoDtoToAppUser(loginResponse.data.user, email);
            const familyAccount = createFamilyAccountFromUserInfo(loginResponse.data.user);
            
            // Set auth tokens
            appUser.authToken = loginResponse.data.token;
            appUser.refreshToken = loginResponse.data.refreshToken;
            
            // Update API service with token
            if (config.apiService) {
              config.apiService.setAuthToken(loginResponse.data.token);
            }
            
            // Store tokens securely
            await config.storage.setItem('auth_token', loginResponse.data.token);
            await config.storage.setItem('refresh_token', loginResponse.data.refreshToken);
            await config.storage.setItem('auth_timestamp', new Date().toISOString());
            await config.storage.setItem('user_email', email);
                        
            // Create authenticated session
            const authenticatedSession: UserSession = {
              user: appUser,
              familyAccount: familyAccount,
              isAuthenticated: true,
              isLoading: false,
              sessionStartTime: new Date().toISOString(),
              lastActivityTime: new Date().toISOString(),
              sessionTimeoutMinutes: 120, // 2 hours
              isOnline: true,
              pendingSyncOperations: 0,
            };
            
            // Update session state
            set((state) => ({
              ...state,
              session: {
                user: appUser,
                familyAccount: familyAccount,
                isAuthenticated: true,
                isLoading: false,
                sessionStartTime: new Date().toISOString(),
                lastActivityTime: new Date().toISOString(),
                sessionTimeoutMinutes: 120,
                isOnline: true,
                pendingSyncOperations: 0,
              },
              familyUsers: [appUser],
            }));
            
            // Debug: Verify the session was set correctly
            const currentState = get();
            
            // Persist user session
            await config.storage.setItem('userSession', JSON.stringify({
              user: appUser,
              familyAccount: familyAccount,
              isAuthenticated: true,
            }));

            // Sync profiles if available in login response
            const profileData = loginResponse.data.profiles;
            const adminHistoryData = loginResponse.data.adminHistory;
            const authorizedUsersData = loginResponse.data.authorizedUsers;
            const connectionsData = loginResponse.data.connections;
            if (profileData?.profiles && profileData.profiles.length > 0) {
              const syncResult = await get().syncProfilesFromResponse(
                profileData, 
                adminHistoryData, 
                appUser.familyId, 
                profileStore
              );
              if (!syncResult.success) {
                console.warn('⚠️ UserStore: Profile sync encountered issues but login continues');
              }
            } else {
              console.log('💡 UserStore: No profiles available for sync in login response');
            }

            // Sync admin history if available in login response
            // Normalise casing: backend may return adminhistory (lowercase) or adminHistory (camelCase)
            const adminHistoryItems = adminHistoryData?.adminHistory ?? (adminHistoryData as any)?.adminhistory;
            if (adminHistoryItems && adminHistoryItems.length > 0) {
              const normalizedAdminHistoryData = { adminHistory: adminHistoryItems };
              const adminSyncResult = await get().syncAdminHistoryFromResponse(
                normalizedAdminHistoryData,
                appUser.familyId
              );
              if (!adminSyncResult.success) {
                console.warn('⚠️ UserStore: Admin history sync encountered issues but login continues');
              }
            } else {
              console.log('💡 UserStore: No admin history available for sync in login response');
            }

            // Sync authorized users if available in login response
            if (authorizedUsersData?.authorizedusers && authorizedUsersData.authorizedusers.length > 0) {
              try {
                const authorizedUserStorage = new AccountUserStorage(config.storage);
                for (const rawUser of authorizedUsersData.authorizedusers) {
                  const dto = AccountUserStorage.convertFromBackendItem(rawUser, appUser.familyId);
                  await authorizedUserStorage.saveAccountUser(appUser.familyId, dto);
                }
              } catch (authUsersError) {
                console.warn('⚠️ UserStore: Authorized users sync encountered issues but login continues', authUsersError);
              }
            } else {
              console.log('💡 UserStore: No authorized users available for sync in login response');
            }

            // Sync connections if available in login response
            if (connectionsData?.connections && connectionsData.connections.length > 0) {
              try {
                const connectionStorage = new ConnectionStorage(config.storage);
                for (const rawConn of connectionsData.connections) {
                  const dto = ConnectionStorage.convertFromBackendItem(rawConn, appUser.familyId);
                  await connectionStorage.saveConnection(appUser.familyId, dto);
                }
              } catch (connError) {
                console.warn('⚠️ UserStore: Connections sync encountered issues but login continues', connError);
              }
            } else {
              console.log('💡 UserStore: No connections available for sync in login response');
            }

            // Fetch account_get_info and apply user settings after login
            try {
              console.log('🔄 UserStore: Fetching account_get_info after login...');
              const accountInfoRequest = {
                RequestType: 'account_get_info',
                Payload: JSON.stringify({ accountId: appUser.familyId }),
              };
              const accountInfoResponse = await config.apiService.processServiceRequestWithRefresh(
                accountInfoRequest,
                'sync',
                { userStore: get, storage: config.storage }
              );
              if (accountInfoResponse?.success && accountInfoResponse?.payload) {
                let accountInfoDto: AccountGetInfoResponseDto;
                if (typeof accountInfoResponse.payload === 'string') {
                  accountInfoDto = JSON.parse(accountInfoResponse.payload);
                } else {
                  accountInfoDto = accountInfoResponse.payload as AccountGetInfoResponseDto;
                }
                const accountUserStorage = new AccountUserStorage(config.storage);
                await accountUserStorage.saveAccountGetInfo(appUser.familyId, accountInfoDto);
                const { appliedCount } = await applyUserSettingsFromDto(accountInfoDto, config.storage);
                void appliedCount;

                // Apply profile connections from account_get_info to the profile store.
                // AccountGetInfoResponseDto.connections contains all connections keyed by ProfileId.
                applyAccountConnectionsToProfileStore(accountInfoDto.connections, profileStore, 'login:account_get_info');
              } else {
                console.warn('⚠️ UserStore: account_get_info returned no data, skipping settings apply');
              }
            } catch (accountInfoError) {
              // Non-fatal — settings apply failure must not block login
              console.warn('⚠️ UserStore: account_get_info fetch failed (non-fatal):', accountInfoError);
            }

            return true;
          } else {
            set((state) => ({ ...state, session: { ...state.session, isLoading: false } }));
            return false;
          }
        } catch (error) {
          console.error('Login failed:', error);
          return false;
        }
      },
      
      logout: async (): Promise<void> => {
        try {
          // Clear API token
          if (config.apiService) {
            config.apiService.setAuthToken(null);
          }
          
          // Clear stored authentication data
          await config.storage.removeItem('userSession');
          await config.storage.removeItem('auth_token');
          await config.storage.removeItem('refresh_token');
          await config.storage.removeItem('user_email');
          
          // Reset to default session
          set((state) => ({
            ...state,
            session: createDefaultUserSession(),
            familyUsers: []
          }));
        } catch (error) {
          console.error('Logout error:', error);
          // Even if storage fails, reset the session
          set((state) => ({
            ...state,
            session: createDefaultUserSession(),
            familyUsers: []
          }));
        }
      },
      
      // Background authentication check
      checkStoredAuth: async (): Promise<boolean> => {
        try {
          console.log('🔍 UserStore: checkStoredAuth called');
          
          const storedToken = await config.storage.getItem('auth_token');
          const storedEmail = await config.storage.getItem('user_email');
          const storedSession = await config.storage.getItem('userSession');
          const storedRefreshToken = await config.storage.getItem('refresh_token');
          
          if (storedToken && storedEmail && storedSession) {
            // Set token in API service if available
            if (config.apiService) {
              config.apiService.setAuthToken(storedToken);
            }
            
            // Restore session from storage
            const sessionData = JSON.parse(storedSession);
            
            // Check if user object needs mapping (old structure to new structure)
            if (sessionData.user) {
              // If user object has old structure (id instead of userId), map it
              const userObj = sessionData.user as any;
              if (!userObj.userId && userObj.id) {
                
                // Map old structure to new
                sessionData.user = {
                  ...userObj,
                  userId: userObj.id,          // Map id -> userId
                  familyId: userObj.accountId, // Map accountId -> familyId
                };
              } else {
                console.log('✅ UserStore: User object already has new structure');
              }
              
              // Ensure the user object has the current tokens (both auth and refresh)
              sessionData.user.authToken = storedToken;
              if (storedRefreshToken) {
                sessionData.user.refreshToken = storedRefreshToken;
              }
            }
             
            const restoredSession = {
              user: sessionData.user,
              familyAccount: sessionData.familyAccount,
              isAuthenticated: true,
              isLoading: false,
              sessionStartTime: sessionData.sessionStartTime || new Date().toISOString(),
              lastActivityTime: new Date().toISOString(),
              sessionTimeoutMinutes: 120,
              isOnline: true,
              pendingSyncOperations: 0,
            };
            
            set((state) => ({
              ...state,
              session: restoredSession,
              familyUsers: sessionData.user ? [sessionData.user] : [],
            }));
            
            // Fetch account_get_info and apply user settings after restoring session
            const restoredFamilyId = sessionData.user?.familyId || (sessionData.user as any)?.accountId;
            if (restoredFamilyId && config.apiService) {
              try {
                console.log('🔄 UserStore: Fetching account_get_info after checkStoredAuth...');
                const accountInfoRequest = {
                  RequestType: 'account_get_info',
                  Payload: JSON.stringify({ accountId: restoredFamilyId }),
                };
                const accountInfoResponse = await config.apiService.processServiceRequestWithRefresh(
                  accountInfoRequest,
                  'sync',
                  { userStore: get, storage: config.storage }
                );
                if (accountInfoResponse?.success && accountInfoResponse?.payload) {
                  let accountInfoDto: AccountGetInfoResponseDto;
                  if (typeof accountInfoResponse.payload === 'string') {
                    accountInfoDto = JSON.parse(accountInfoResponse.payload);
                  } else {
                    accountInfoDto = accountInfoResponse.payload as AccountGetInfoResponseDto;
                  }
                  const accountUserStorage = new AccountUserStorage(config.storage);
                  await accountUserStorage.saveAccountGetInfo(restoredFamilyId, accountInfoDto);
                  await applyUserSettingsFromDto(accountInfoDto, config.storage);

                  // Apply profile connections — use globalThis profile store reference
                  // (checkStoredAuth has no profileStore parameter; the store is accessible
                  // via the global reference set by createStoresWithStorage).
                  const globalProfileStore =
                    (globalThis as any).__profileStore ??
                    (globalThis as any).__childNavStores?.useProfileStore;
                  applyAccountConnectionsToProfileStore(accountInfoDto.connections, globalProfileStore, 'checkStoredAuth:account_get_info');
                } else {
                  console.warn('⚠️ UserStore: account_get_info returned no data during checkStoredAuth');
                }
              } catch (accountInfoError) {
                console.warn('⚠️ UserStore: account_get_info fetch failed during checkStoredAuth (non-fatal):', accountInfoError);
              }
            }

            return true;
          }
          
          return false;
        } catch (error) {
          console.error('Stored auth check error:', error);
          return false;
        }
      },
      
      refreshAuth: async (): Promise<boolean> => {
        try {
          const sessionData = await config.storage.getItem('userSession');
          const storedToken = await config.storage.getItem('auth_token');
          const storedRefreshToken = await config.storage.getItem('refresh_token');
          
          if (sessionData && storedToken) {
            const parsed = JSON.parse(sessionData);
            
            // Apply old→new structure mapping (same as checkStoredAuth)
            // Stored sessions may have {id, accountId} instead of {userId, familyId}
            if (parsed.user) {
              const userObj = parsed.user as any;
              if (!userObj.userId && userObj.id) {
                parsed.user = {
                  ...userObj,
                  userId: userObj.id,
                  familyId: userObj.accountId,
                };
              }
              // Always ensure current tokens are attached
              parsed.user.authToken = storedToken;
              if (storedRefreshToken) {
                parsed.user.refreshToken = storedRefreshToken;
              }
            }
            
            set((state) => ({
              session: {
                ...state.session,
                user: parsed.user,
                familyAccount: parsed.familyAccount,
                isAuthenticated: true,
                lastActivityTime: new Date().toISOString(),
                sessionStartTime: parsed.sessionStartTime || new Date().toISOString(),
                sessionTimeoutMinutes: 120,
                isOnline: true,
                pendingSyncOperations: 0,
              },
              familyUsers: parsed.user ? [parsed.user] : [],
            }));
            
            // IMPORTANT: Also refresh the API service auth token
            if (config.apiService) {
              config.apiService.setAuthToken(storedToken);
            }
            
            return true;
          }
          return false;
        } catch (error) {
          console.error('Auth refresh failed:', error);
          return false;
        }
      },
      
      // Helper method to access storage items
      getStoredItem: async (key: string): Promise<string | null> => {
        try {
          return await config.storage.getItem(key);
        } catch (error) {
          console.error(`Error getting stored item ${key}:`, error);
          return null;
        }
      },
      
      // User management
      updateUser: async (updates: Partial<AppUser>): Promise<void> => {
        const currentUser = get().session.user;
        if (!currentUser) return;
        
        const updatedUser = { ...currentUser, ...updates, lastActiveDate: new Date().toISOString() };
        
        set((state) => ({
          session: {
            ...state.session,
            user: updatedUser,
            lastActivityTime: new Date().toISOString(),
          },
          familyUsers: state.familyUsers.map(user => 
            user.userId === updatedUser.userId ? updatedUser : user
          ),
        }));
        
        // Persist updated user
        const sessionData = await config.storage.getItem('userSession');
        if (sessionData) {
          const parsed = JSON.parse(sessionData);
          parsed.user = updatedUser;
          await config.storage.setItem('userSession', JSON.stringify(parsed));
        }
      },
      
      updatePreferences: async (preferences: Partial<AppUser['preferences']>): Promise<void> => {
        const currentUser = get().session.user;
        if (!currentUser) return;
        
        await get().updateUser({
          preferences: { ...currentUser.preferences, ...preferences }
        });
      },
      
      updateAppState: async (appState: Partial<AppUser['appState']>): Promise<void> => {
        const currentUser = get().session.user;
        if (!currentUser) return;
        
        await get().updateUser({
          appState: { ...currentUser.appState, ...appState }
        });
      },
      
      // Family management
      inviteUser: async (email: string, role: AppUser['role']): Promise<string> => {
        // TODO: Implement user invitation logic
        return 'invitation-id-placeholder';
      },
      
      removeUser: async (userId: string): Promise<void> => {
        set((state) => ({
          familyUsers: state.familyUsers.filter(user => user.userId !== userId),
        }));
      },
      
      updateUserRole: async (userId: string, role: AppUser['role']): Promise<void> => {
        set((state) => ({
          familyUsers: state.familyUsers.map(user => 
            user.userId === userId 
              ? { ...user, role, lastActiveDate: new Date().toISOString() }
              : user
          ),
        }));
      },
      
      // Profile permissions
      updateProfilePermissions: async (profileId: string, permissions: AppUser['profilePermissions'][string]): Promise<void> => {
        const currentUser = get().session.user;
        if (!currentUser) return;
        
        await get().updateUser({
          profilePermissions: {
            ...currentUser.profilePermissions,
            [profileId]: permissions,
          }
        });
      },
      
      canAccessProfile: (profileId: string): boolean => {
        const currentUser = get().session.user;
        if (!currentUser) return false;
        
        const permissions = currentUser.profilePermissions[profileId];
        return permissions?.canView ?? false;
      },
      
      canEditProfile: (profileId: string): boolean => {
        const currentUser = get().session.user;
        if (!currentUser) return false;
        
        const permissions = currentUser.profilePermissions[profileId];
        return permissions?.canEdit ?? false;
      },
      
      // Session management
      extendSession: (): void => {
        set((state) => ({
          session: {
            ...state.session,
            lastActivityTime: new Date().toISOString(),
          },
        }));
      },
      
      isSessionValid: (): boolean => {
        const session = get().session;
        if (!session.isAuthenticated || !session.user) return false;
        
        const now = new Date().getTime();
        const lastActivity = new Date(session.lastActivityTime).getTime();
        const timeoutMs = session.sessionTimeoutMinutes * 60 * 1000;
        
        return (now - lastActivity) < timeoutMs;
      },
      
      checkSessionTimeout: (): boolean => {
        const isValid = get().isSessionValid();
        if (!isValid && get().session.isAuthenticated) {
          // Auto-logout on timeout
          get().logout();
        }
        return isValid;
      },
      
      // Utility getters
      getCurrentUser: (): AppUser | null => {
        return get().session.user;
      },
      
      getFamilyAccount: (): FamilyAccount | null => {
        return get().session.familyAccount;
      },
      
      getSelectedProfileId: (): string | null => {
        const currentUser = get().session.user;
        return currentUser?.appState.selectedProfileId ?? null;
      },
      
      isPremiumUser: (): boolean => {
        const familyAccount = get().session.familyAccount;
        return familyAccount?.accountTier === 'premium';
      },
      
      canInviteUsers: (): boolean => {
        const currentUser = get().session.user;
        return currentUser?.role === 'primary';
      },
      
      // Configuration management
      setApiService: (apiService: ApiService): void => {
        config.apiService = apiService;
      },
      
      getApiService: (): ApiService | null => {
        return config.apiService || null;
      },

      // Profile synchronization - Common function for login and token refresh
      syncProfilesFromResponse: async (
        profileData?: ProfileGetListResponseDto,
        adminHistoryData?: AdminHistoryGetListResponseDto,
        accountId?: string,
        profileStore?: any
      ): Promise<{ success: boolean; message: string; syncResults?: any }> => {
        try {

          if (!profileData?.profiles || profileData.profiles.length === 0) {
            return { success: true, message: 'No profiles to sync' };
          }

          if (!profileStore) {
            console.warn('⚠️ UserStore: Profile store not available - cannot sync profiles');
            return { success: false, message: 'Profile store not available' };
          }

          const serverProfiles = profileData.profiles || [];
          const profileStoreState = profileStore.getState();
          const localProfiles = profileStoreState.profiles;
          const resolvedAccountId = accountId || get().session?.user?.familyId || 'default-account';

          // Date normalization utility
          const normalizeDate = (dateString: string): Date => {
            if (dateString && !dateString.includes('Z') && !dateString.includes('+')) {
              return new Date(dateString + 'Z');
            }
            return new Date(dateString);
          };

          // Check if sync is needed (2-minute threshold)
          const shouldSyncProfile = (serverModified: string, localSyncDate?: string): boolean => {
            if (!localSyncDate) return true;
            const serverDate = normalizeDate(serverModified);
            const localDate = normalizeDate(localSyncDate);
            const diffMs = Math.abs(serverDate.getTime() - localDate.getTime());
            return diffMs > (2 * 60 * 1000); // 2 minutes
          };

          // Determine which profiles need syncing
          const profilesToSync = [];
          let syncedCount = 0;
          let addedCount = 0;
          let updatedCount = 0;

          for (const serverProfile of serverProfiles) {
            const localProfile = localProfiles.find((p: any) => p.id === serverProfile.profileId);
            
            if (!localProfile) {
              profilesToSync.push({ profileId: serverProfile.profileId, reason: 'NEW' });
              addedCount++;
            } else {
              if (shouldSyncProfile(serverProfile.modified, localProfile.lastSyncDate)) {
                profilesToSync.push({ profileId: serverProfile.profileId, reason: 'UPDATE' });
                updatedCount++;
              } else {
                console.log(`   ✅ Profile up to date: ${serverProfile.profileId}`);
              }
            }
            syncedCount++;
          }

          // Perform actual sync operations
          if (profilesToSync.length > 0 && config.apiService) {
            
            let syncResults = { successful: 0, failed: 0, errors: [] as string[] };
            
            for (const profileToSync of profilesToSync) {
              try {
                console.log(`   📥 Fetching profile: ${profileToSync.profileId}`);
                
                // Create profile_get request
                const request = {
                  RequestType: 'profile_get',
                  Payload: JSON.stringify({ profileId: profileToSync.profileId, accountId: resolvedAccountId })
                };
                
                // Fetch profile from server with automatic 401 retry handling
                const response = await config.apiService.processServiceRequestWithRefresh(request, 'sync', {
                  userStore: { getState: get },
                  storage: config.storage,
                });
                
                if (response?.success) {
                  // Parse the response payload
                  let appProfileDto;
                  try {
                    if (typeof response.payload === 'string') {
                      appProfileDto = JSON.parse(response.payload);
                    } else {
                      appProfileDto = response.payload;
                    }
                  } catch (parseError) {
                    throw new Error(`Failed to parse profile data: ${parseError instanceof Error ? parseError.message : String(parseError)}`);
                  }
                  
                  console.log(`   ✅ Fetched profile data for ${profileToSync.profileId}`);
                  
                  // Store in AppProfileStorage
                  try {
                    const appProfileStorage = new AppProfileStorage(config.storage);
                    await appProfileStorage.saveProfile(appProfileDto);
                  } catch (appStorageError) {
                    console.warn(`   ⚠️ AppProfileStorage failed for ${profileToSync.profileId}:`, appStorageError);
                  }
                  
                  // Check if this is a new profile or an update
                  const existingProfile = localProfiles.find((p: any) => p.id === profileToSync.profileId);
                  if (existingProfile) {
                    // Update existing profile - do a full conversion so actionsstate and all
                    // other profile fields are refreshed, not just the sync metadata.
                    try {
                      const convertedProfile = convertAppProfileToChildProfile(appProfileDto, config.storage);
                      
                      // If server marks this profile as deleted, remove it locally
                      if (convertedProfile.profileStatus === -1) {
                        console.log(`   🗑️ Removing locally deleted profile ${profileToSync.profileId}`);
                        if (typeof profileStoreState.removeProfile === 'function') {
                          profileStoreState.removeProfile(profileToSync.profileId);
                        }
                        syncResults.successful++;
                        continue;
                      }
                      
                      // If the server returned no actions, preserve the local copy so we don't
                      // accidentally wipe actions that were saved locally but not yet included
                      // in this particular profile_get response.
                      const serverActionsCount = appProfileDto.actions?.length ?? appProfileDto.Actions?.length ?? 0;
                      const profileModified = appProfileDto.modified ?? appProfileDto.Modified ?? new Date().toISOString();
                      const preservedActionsState = serverActionsCount > 0
                        ? convertedProfile.actionsstate
                        : existingProfile.actionsstate;
                      const hasServerPrescriptionTrack = !!(
                        appProfileDto.prescriptions ??
                        (appProfileDto as any).Prescriptions ??
                        (appProfileDto as any).prescriptiontrack ??
                        (appProfileDto as any).PrescriptionTrack
                      );
                      const hasServerReferralTrack = !!(
                        appProfileDto.referrals ??
                        (appProfileDto as any).Referrals ??
                        (appProfileDto as any).referraltrack ??
                        (appProfileDto as any).ReferralTrack
                      );
                      profileStoreState.updateProfile(profileToSync.profileId, {
                        ...convertedProfile,
                        id: existingProfile.id,
                        lastSyncDate: profileModified,
                        isDirty: false,
                        syncFailureReason: undefined,
                        actionsstate: preservedActionsState,
                        prescriptiontrack: hasServerPrescriptionTrack
                          ? convertedProfile.prescriptiontrack
                          : existingProfile.prescriptiontrack,
                        referraltrack: hasServerReferralTrack
                          ? convertedProfile.referraltrack
                          : existingProfile.referraltrack,
                      });
                    } catch (conversionError) {
                      // Fallback: at minimum update sync metadata so we don't loop forever
                      console.warn(`   ⚠️ Full profile conversion failed for ${profileToSync.profileId}, falling back to metadata-only update:`, conversionError);
                      const profileModified = appProfileDto.modified ?? appProfileDto.Modified ?? new Date().toISOString();
                      profileStoreState.updateProfile(profileToSync.profileId, {
                        lastSyncDate: profileModified,
                        isDirty: false,
                        syncFailureReason: undefined,
                      });
                    }
                  } else {
                    // Add new profile - convert AppProfileDto to ChildProfile format                    
                    try {
                      // Convert AppProfileDto to ChildProfile with storage for AdminState processing
                      const childProfile = convertAppProfileToChildProfile(appProfileDto, config.storage);
                      
                      // Skip profiles deleted on the server
                      if (childProfile.profileStatus === -1) {
                        console.log(`   🗑️ Skipping deleted profile ${profileToSync.profileId}`);
                        syncResults.successful++;
                        continue;
                      }
                      
                      // Add profile to store
                      if (typeof profileStoreState.addProfile === 'function') {
                        // skipBackendSync=true: profile came FROM the server, no need to upload it back
                        await profileStoreState.addProfile(childProfile, { skipBackendSync: true });
                        
                        // Verify the profile was added with correct ID
                        const addedProfile = profileStoreState.profiles.find((p: any) => p.id === profileToSync.profileId);
                      } else {
                        console.error(`   ❌ addProfile method not available on profile store`);
                      }
                    } catch (conversionError) {
                      console.error(`   ❌ Failed to convert AppProfileDto to ChildProfile:`, conversionError);
                      throw conversionError;
                    }
                  }
                  
                  syncResults.successful++;
                } else {
                  throw new Error(`Server error: ${response?.message || 'Unknown error'}`);
                }
              } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                console.error(`   ❌ Failed to sync ${profileToSync.profileId}:`, errorMessage);
                syncResults.failed++;
                syncResults.errors.push(`${profileToSync.profileId}: ${errorMessage}`);
              }
            }
            
            if (syncResults.errors.length > 0) {
              console.log('   ❌ Sync Errors:', syncResults.errors);
            }
            
            const message = `Profile sync completed (${syncResults.successful}/${profilesToSync.length} profiles synced)`;
            return { success: syncResults.failed === 0, message, syncResults };
          } else {
            return { success: true, message: `All profiles up to date (${syncedCount} profiles checked)` };
          }

        } catch (error) {
          console.error('🔄 UserStore: Profile sync error:', error);
          const errorMessage = error instanceof Error ? error.message : String(error);
          return { success: false, message: `Profile sync failed: ${errorMessage}` };
        }
      },

      // Admin History synchronization - Common function for login and token refresh
      syncAdminHistoryFromResponse: async (
        adminHistoryData?: AdminHistoryGetListResponseDto,
        accountId?: string
      ): Promise<{ success: boolean; message: string; syncResults?: any }> => {
        try {

          if (!adminHistoryData?.adminHistory || adminHistoryData.adminHistory.length === 0) {
            return { success: true, message: 'No admin history to sync' };
          }

          // Normalise casing one final time in case caller did not pre-normalise
          const serverAdminHistory: AdminHistoryGetListItemDto[] =
            adminHistoryData.adminHistory ?? (adminHistoryData as any).adminhistory ?? [];
          const resolvedAccountId = accountId || get().session?.user?.familyId || 'default-account';

          // Group admin history by profileId for efficient processing
          // Backend sends profileId as 'id' and accountId as 'recPractice'
          const adminHistoryByProfile = new Map<string, typeof serverAdminHistory>();
          serverAdminHistory.forEach((item: any) => {
            const profileId = item.id ?? item.profileId; // Backend sends 'id', DTO legacy fallback 'profileId'
            if (!adminHistoryByProfile.has(profileId)) {
              adminHistoryByProfile.set(profileId, []);
            }
            adminHistoryByProfile.get(profileId)!.push(item);
          });

          console.log('   👥 Admin history spans profiles:', adminHistoryByProfile.size);

          // Determine which admin responses need syncing
          const responsesToSync = [];
          let checkedCount = 0;
          let skippedCount = 0;
          let newCount = 0;
          let updateCount = 0;

          // Initialize AdminResponseStorage
          const adminResponseStorage = new AdminResponseStorage(config.storage);

          for (const [profileId, historyItems] of adminHistoryByProfile.entries()) {
            try {
              // Get existing responses for this profile
              const localResponses = await adminResponseStorage.getProfileResponses(profileId);
              
              for (const historyItem of historyItems) {
                checkedCount++;

                // Check if we already have this response locally
                // Match on: recPractice (accountId), id (profileId), and recUTM
                // Note: local recId is a placeholder and should not be used for comparison
                // Note: Backend truncates fractional seconds, so normalize timestamps for comparison
                const normalizeRecUTM = (timestamp: string): string => {
                  if (!timestamp) return '';
                  // Remove fractional seconds (.xxx) from timestamp for comparison
                  // Example: "2026-02-16T14:30:45.123Z" -> "2026-02-16T14:30:45Z"
                  return timestamp.replace(/\.\d{3}Z?$/, 'Z');
                };
                
                const existingResponse = localResponses.responses.find(local => {
                  // If both server and local have recId → match on recId alone (most reliable)
                  if (historyItem.recId && local.recId) {
                    return local.recId === historyItem.recId;
                  }
                  // Otherwise fall back to profileId (id) + recUTM
                  return (
                    local.id === profileId &&
                    normalizeRecUTM(local.recUTM) === normalizeRecUTM(historyItem.recUTM)
                  );
                });

                if (!existingResponse) {
                  const recId = historyItem.recId;
                  const resolvedProfileId = (historyItem as any).id ?? historyItem.profileId;
                  responsesToSync.push({ 
                    recId: recId, 
                    profileId: resolvedProfileId,
                    accountId: (historyItem as any).recPractice ?? resolvedAccountId,
                    recUTM: historyItem.recUTM,
                    reason: 'NEW' 
                  });
                  newCount++;
                } else {
                  // Re-download if the existing record is incomplete (saved by a previous broken sync).
                  // A record is considered incomplete if responseData is empty AND key display fields are blank.
                  const isIncomplete =
                    (!existingResponse.responseData || existingResponse.responseData.length === 0) &&
                    !existingResponse.recSurvey &&
                    !existingResponse.recAlias &&
                    !existingResponse.recDate;

                  if (isIncomplete) {
                    const recId = historyItem.recId;
                    const resolvedProfileId = (historyItem as any).id ?? historyItem.profileId;
                    responsesToSync.push({
                      recId: recId,
                      profileId: resolvedProfileId,
                      accountId: (historyItem as any).recPractice ?? resolvedAccountId,
                      recUTM: historyItem.recUTM,
                      reason: 'INCOMPLETE'
                    });
                    updateCount++;
                  } else {
                    const recId = historyItem.recId;
                    skippedCount++;
                  }
                }
              }
            } catch (profileError) {
              console.warn(`   ⚠️ Error checking responses for profile ${profileId}:`, profileError);
            }
          }

          // Perform actual sync operations
          if (responsesToSync.length > 0 && config.apiService) {
            
            let syncResults = { successful: 0, failed: 0, errors: [] as string[] };
            
            try {
              // Create batch request with all admin response requests
              const adminResponseRequests = responsesToSync.map(responseToSync => {
                // Find the original history item to get RecUTM
                const historyItem = serverAdminHistory.find((item: any) => {
                  const itemRecId = item.recId; // Server uses lowercase 'recId'
                  const itemProfileId = item.id ?? item.profileId; // Backend sends 'id'
                  return itemRecId === responseToSync.recId && itemProfileId === responseToSync.profileId;
                });
                
                if (!historyItem) {
                  console.warn(`   ⚠️ Could not find matching historyItem for RecId: ${responseToSync.recId}, ProfileId: ${responseToSync.profileId}`);
                }
                
                const requestDto = {
                  RecId: responseToSync.recId,
                  AccountId: responseToSync.accountId || resolvedAccountId,
                  ProfileId: responseToSync.profileId,
                  RecUTM: responseToSync.recUTM || historyItem?.recUTM || ''
                };
                
                return requestDto;
              });

              const batchRequest = {
                RequestType: 'admin_response_get',
                Payload: JSON.stringify({ 
                  AdminResponseRequests: adminResponseRequests
                })
              };
              
              // Fetch admin responses from server with automatic 401 retry handling
              const response = await config.apiService.processServiceRequestWithRefresh(batchRequest, 'data', {
                userStore: { getState: get },
                storage: config.storage,
              });
              
              if (response?.success) {
                // Parse the response payload
                let adminResponseListDto: { AdminResponses: AdminResponseDto[] };
                try {
                  if (typeof response.payload === 'string') {
                    adminResponseListDto = JSON.parse(response.payload);
                  } else {
                    adminResponseListDto = response.payload;
                  }
                } catch (parseError) {
                  throw new Error(`Failed to parse admin response list data: ${parseError instanceof Error ? parseError.message : String(parseError)}`);
                }
                


                // Normalize C# PascalCase server response fields to camelCase TS interface fields
                const normalizeServerResponse = (raw: any): AdminResponseDto => ({
                  recId:         raw.RecId         ?? raw.recId         ?? '',
                  recStatus:     raw.RecStatus      ?? raw.recStatus     ?? '',
                  recDate:       raw.RecDate        ?? raw.recDate       ?? '',
                  recUTM:        raw.RecUTM         ?? raw.recUTM        ?? '',
                  id:            raw.ID             ?? raw.id            ?? '',
                  recPatient:    raw.RecPatient     ?? raw.recPatient    ?? '',
                  recAssessment: raw.RecAssessment  ?? raw.recAssessment ?? '',
                  surveybList:   raw.SurveybList    ?? raw.surveybList   ?? '',
                  recSurvey:     raw.RecAssessment  ?? raw.recSurvey     ?? raw.recAssessment ?? '',
                  surveyAliasList: raw.SurveybList  ?? raw.surveyAliasList ?? '',
                  recClient:     raw.RecClient      ?? raw.recClient     ?? '',
                  recPractice:   raw.RecPractice    ?? raw.recPractice   ?? '',
                  recSite:       raw.RecSite        ?? raw.recSite       ?? '',
                  recStation:    raw.RecStation     ?? raw.recStation    ?? '',
                  recSN:         raw.RecSN          ?? raw.recSN         ?? '',
                  recYear:       raw.RecYear        ?? raw.recYear       ?? '',
                  recQtr:        raw.RecQtr         ?? raw.recQtr        ?? '',
                  recMon:        raw.RecMon         ?? raw.recMon        ?? '',
                  recWeek:       raw.RecWeek        ?? raw.recWeek       ?? '',
                  recDay:        raw.RecDay         ?? raw.recDay        ?? '',
                  duration:      raw.Duration       ?? raw.duration      ?? '',
                  recProvider:   raw.RecProvider    ?? raw.recProvider   ?? '',
                  recLang:       raw.RecLang        ?? raw.recLang       ?? '',
                  recAlias:      raw.RecAlias       ?? raw.recAlias      ?? '',
                  recPeriod:     raw.RecPeriod      ?? raw.recPeriod     ?? '',
                  seq_Num:       raw.Seq_Num        ?? raw.seq_Num       ?? '',
                  recstate:      raw.Recstate       ?? raw.recstate      ?? '',
                  recFirst:      raw.RecFirst       ?? raw.recFirst      ?? '',
                  recDepartment: raw.RecDepartment  ?? raw.recDepartment ?? '',
                  recSpecialty:  raw.RecSpecialty   ?? raw.recSpecialty  ?? '',
                  recGroup:      raw.RecGroup       ?? raw.recGroup      ?? '',
                  recLTM:        raw.RecLTM         ?? raw.recLTM        ?? '',
                  id_RC:         raw.Id_RC          ?? raw.id_RC         ?? '',
                  responseData: (raw.ResponseData   ?? raw.responseData  ?? []).map((d: any) => ({
                    name:  d.Name  ?? d.name  ?? '',
                    value: d.Value ?? d.value ?? '',
                  })),
                });

                const normalizeUTM = (ts: string) => (ts || '').replace(/\.\d{3}Z?$/, 'Z');

                // Process each admin response from the batch
                const adminResponses = (adminResponseListDto.AdminResponses || []).map(normalizeServerResponse);
                for (const adminResponseDto of adminResponses) {
                  try {
                    // Match strategy:
                    //   • If req has a recId AND dto has a recId → match on recId alone
                    //   • Otherwise → match on profileId (id) + recUTM
                    const matchingRequest = responsesToSync.find(req => {
                      if (req.recId && adminResponseDto.recId) {
                        return req.recId === adminResponseDto.recId;
                      }
                      return (
                        req.profileId === adminResponseDto.id &&
                        normalizeUTM(req.recUTM || '') === normalizeUTM(adminResponseDto.recUTM)
                      );
                    });

                    if (matchingRequest) {
                      // If local record was missing recId, the server-normalized dto already has it
                      const saved = await adminResponseStorage.saveAdminResponse(matchingRequest.profileId, adminResponseDto);
                      if (saved) {
                        syncResults.successful++;
                      } else {
                        throw new Error('Failed to save admin response locally');
                      }
                    } else {
                      console.warn(`   ⚠️ Could not match admin response RecId:${adminResponseDto.recId} Id:${adminResponseDto.id} RecUTM:${adminResponseDto.recUTM} to a request`);
                      syncResults.failed++;
                      syncResults.errors.push(`${adminResponseDto.recId || adminResponseDto.id}: Could not match to request`);
                    }
                  } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : String(error);
                    console.error(`   ❌ Failed to process admin response RecId:${adminResponseDto.recId}:`, errorMessage);
                    syncResults.failed++;
                    syncResults.errors.push(`${adminResponseDto.recId || adminResponseDto.id}: ${errorMessage}`);
                  }
                }
              } else {
                throw new Error(`Server error: ${response?.message || 'Unknown error'}`);
              }
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : String(error);
              console.log(`   ❌ Failed to sync admin responses batch:`, errorMessage);
              // If the entire batch fails, mark all as failed
              responsesToSync.forEach(responseToSync => {
                syncResults.failed++;
                syncResults.errors.push(`${responseToSync.recId}: ${errorMessage}`);
              });
            }
            
            if (syncResults.errors.length > 0) {
              console.warn('   ❌ Admin sync errors:', syncResults.errors);
            }
            
            const message = `Admin history sync completed (${syncResults.successful}/${responsesToSync.length} responses synced)`;
            return { success: syncResults.failed === 0, message, syncResults };
          } else {
            console.log('🔄 UserStore: All admin responses are up to date');
            return { success: true, message: `All admin responses up to date (${checkedCount} responses checked)` };
          }

        } catch (error) {
          console.error('🔄 UserStore: Admin history sync error:', error);
          const errorMessage = error instanceof Error ? error.message : String(error);
          return { success: false, message: `Admin history sync failed: ${errorMessage}` };
        }
      },

      // Common backend synchronization function - used by login, token refresh, and debug operations
      syncBackendFromResponse: async (options: {
        profileData?: ProfileGetListResponseDto;
        adminHistoryData?: AdminHistoryGetListResponseDto;
        familyId?: string;
        profileStore?: any;
        contextDescription: string; // 'login', 'token_refresh', 'debug', etc.
      }): Promise<{ 
        success: boolean; 
        message: string; 
        results: {
          profile?: { success: boolean; message: string; syncResults?: any };
          adminHistory?: { success: boolean; message: string; syncResults?: any };
        };
        errors: string[];
      }> => {
        try {

          const results: any = {};
          const errors: string[] = [];
          let overallSuccess = true;

          // Sync profiles if available
          if (options.profileData?.profiles && options.profileData.profiles.length > 0) {
            try {
              results.profile = await get().syncProfilesFromResponse(
                options.profileData,
                options.adminHistoryData,
                options.familyId,
                options.profileStore
              );
              if (!results.profile.success) {
                overallSuccess = false;
                errors.push(`Profile sync: ${results.profile.message}`);
              }
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : String(error);
              results.profile = { success: false, message: `Profile sync failed: ${errorMessage}` };
              overallSuccess = false;
              errors.push(`Profile sync error: ${errorMessage}`);
            }
          } else {
            results.profile = { success: true, message: 'No profiles to sync' };
          }

          // Sync admin history if available
          // Normalise casing: backend may return adminhistory (lowercase) or adminHistory (camelCase)
          const adminHistoryItems = options.adminHistoryData?.adminHistory ?? (options.adminHistoryData as any)?.adminhistory;
          if (adminHistoryItems && adminHistoryItems.length > 0) {
            try {
              const normalizedAdminHistoryData = { adminHistory: adminHistoryItems };
              results.adminHistory = await get().syncAdminHistoryFromResponse(
                normalizedAdminHistoryData,
                options.familyId
              );
              if (!results.adminHistory.success) {
                overallSuccess = false;
                errors.push(`Admin history sync: ${results.adminHistory.message}`);
              }
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : String(error);
              results.adminHistory = { success: false, message: `Admin history sync failed: ${errorMessage}` };
              overallSuccess = false;
              errors.push(`Admin history sync error: ${errorMessage}`);
            }
          } else {
            results.adminHistory = { success: true, message: 'No admin history to sync' };
          }

          // ── Post-profile-sync: apply stored profile connections ────────────────
          // checkStoredAuth also calls applyAccountConnectionsToProfileStore, but it
          // runs BEFORE profiles are added here.  Re-apply now that profiles exist.
          try {
            const familyId = options.familyId || get().session?.user?.familyId;
            if (familyId && options.profileStore) {
              const aus = new AccountUserStorage(config.storage);
              const storedInfo = await aus.getAccountGetInfo(familyId);
              if (storedInfo?.connections?.length) {
                console.log(`🔗 syncBackendFromResponse [${options.contextDescription}]: applying ${storedInfo.connections.length} stored connection(s) from AccountGetInfo to profile store`);
                applyAccountConnectionsToProfileStore(storedInfo.connections, options.profileStore, `syncBackendFromResponse:${options.contextDescription}`);
              } else {
                console.log(`🔗 syncBackendFromResponse [${options.contextDescription}]: no stored AccountGetInfo connections found for familyId=${familyId}`);
              }
            } else {
              console.log(`🔗 syncBackendFromResponse [${options.contextDescription}]: skipping connection apply — familyId=${familyId}, profileStore=${!!options.profileStore}`);
            }
          } catch (connErr) {
            console.warn(`🔗 syncBackendFromResponse [${options.contextDescription}]: connection apply step failed (non-fatal):`, connErr);
          }

          const successfulSyncs = Object.values(results).filter((r: any) => r.success).length;
          const totalSyncs = Object.keys(results).length;
          const message = overallSuccess 
            ? `All backend sync operations completed successfully (${successfulSyncs}/${totalSyncs})` 
            : `Backend sync completed with ${errors.length} issues (${successfulSyncs}/${totalSyncs} successful)`;

          if (errors.length > 0) {
            console.warn(`🔄 UserStore: Backend sync (${options.contextDescription}) errors:`, errors);
          }

          return {
            success: overallSuccess,
            message: message,
            results: results,
            errors: errors
          };

        } catch (error) {
          console.error(`🔄 UserStore: Backend sync (${options.contextDescription}) error:`, error);
          const errorMessage = error instanceof Error ? error.message : String(error);
          return {
            success: false,
            message: `Backend sync failed: ${errorMessage}`,
            results: {},
            errors: [errorMessage]
          };
        }
      },
      
      // State reset
      reset: (): void => {
        set(() => ({
          session: createDefaultUserSession(),
          familyUsers: [],
        }));
      },
    }))
  );
};

// Export type for the user store
export type UserStore = ReturnType<typeof createUserStore>;